<script setup lang="ts">
import { computed, ref } from 'vue';

import useField from '@/composables/useField';

import fieldStyles from '@/styles/field.module.scss';

export type FieldSize = 'xs' | 'sm' | 'md' | 'lg';

export interface Props {
  modelValue?: number | string | null;
  label?: string;
  size?: FieldSize;
  readonly?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: undefined,
  size: 'md',
  readonly: false,
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: Exclude<(typeof props)['modelValue'], number>];
}>();

const fieldValue = ref(props.modelValue);

useField(props, fieldValue, (newFieldValue) => {
  emit(
    'update:modelValue',
    newFieldValue as Exclude<(typeof props)['modelValue'], number>,
  );
});

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-text-field
    v-model="fieldValue"
    :class="['g-v-text-field', $style.kTextField, fieldStyles[sizeClass]]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :label="props.label"
    :readonly="props.readonly"
    :disabled="props.disabled"
    data-testid="kTextField"
  />
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.kTextField {
  :global {
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}
</style>
